// Skizze zum Ziehen (30.10.2026, Betreiber: „nicht tippen – drücken, halten, Finger herumziehen, erst beim Loslassen
// rastet die Tür an der letzten Stelle ein“). Grundlage für den Flur (Grundriss) und die Türen je Raum.
//
// Bedienung: Finger auf die Skizze legen und kurz halten (HALTEN_MS), dann ziehen – eine neue Tür folgt dem Finger und
// springt an die nächste Wand; liegt der Finger auf einer vorhandenen Tür, wird diese verschoben. Loslassen setzt sie.
// Kurz antippen einer Tür wählt sie aus (Raum ändern, löschen). Wer nur wischt, scrollt die Seite wie gewohnt: Erst nach
// dem Halten blockiert die Skizze das Scrollen (nicht-passiver touchmove-Listener).

import { useEffect, useRef, useState, type ReactNode } from "react";

export type Pt = { x: number; y: number };
/** Eine Wand, an die Türen einrasten: von a nach b, out zeigt vom Gang weg (dort steht die Beschriftung). */
export type Wand = { id: string; a: Pt; b: Pt; out: Pt };
/** Verschiebbarer Griff mit eigenen Wänden (Wohnungstür, Abzweigstelle; seit 31.10.2026 auch Ecken entlang ihres Gangs). */
export type Griff = { key: string; wall: Wand; pos: number; walls: Wand[]; label: string; art: "eingang" | "punkt" };
export type SkizzenTuer = { key: string; wall: string; pos: number; nr: string; label: string; aktiv?: boolean; fest?: boolean };

const HALTEN_MS = 280;
const WACKELN_PX = 10;
/** Wie nah (in Skizzen-Einheiten) der Finger einer Wand kommen muss, damit die Tür einrastet. */
const FANG = 70;
const TUER_HALB = 14;

const lerp = (w: Wand, t: number): Pt => ({ x: w.a.x + (w.b.x - w.a.x) * t, y: w.a.y + (w.b.y - w.a.y) * t });

/** Nächste Wand zu einem Punkt: Lage 0..1 (mit Abstand zu den Ecken) und Abstand. */
export function snap(walls: Wand[], p: Pt): { wall: Wand; pos: number; dist: number } | null {
  let best: { wall: Wand; pos: number; dist: number } | null = null;
  for (const w of walls) {
    const dx = w.b.x - w.a.x;
    const dy = w.b.y - w.a.y;
    const len2 = dx * dx + dy * dy || 1;
    const t = Math.min(0.97, Math.max(0.03, ((p.x - w.a.x) * dx + (p.y - w.a.y) * dy) / len2));
    const q = lerp(w, t);
    const dist = Math.hypot(p.x - q.x, p.y - q.y);
    if (!best || dist < best.dist) best = { wall: w, pos: Math.round(t * 1000) / 1000, dist };
  }
  return best && best.dist <= FANG ? best : null;
}

type Zug = { key: string | null; start: { x: number; y: number }; pointerId: number; timer: number; dragging: boolean; walls: Wand[] };

export function ZiehSkizze(props: {
  walls: Wand[];
  doors: SkizzenTuer[];
  /** Hintergrund (Gang, Raum, Eingang) als SVG-Elemente in Skizzen-Koordinaten. */
  children: ReactNode;
  box: { x: number; y: number; w: number; h: number };
  label: string;
  onDrop: (key: string | null, wall: string, pos: number) => void;
  onTap: (key: string) => void;
  griffe?: Griff[];
  /** Weitere Punkte, die Beschriftungen nicht überdecken sollen (31.10.2026). */
  hindernisse?: Pt[];
}) {
  const svg = useRef<SVGSVGElement>(null);
  const zug = useRef<Zug | null>(null);
  const [geist, setGeist] = useState<{ p: Pt; snap: ReturnType<typeof snap> } | null>(null);
  const [haltend, setHaltend] = useState(false);

  // Breiter als der Rahmen (großer Flur, 31.10.2026): waagerecht auf x = 0 mitteln, dort liegt der Eingang.
  useEffect(() => {
    const el = svg.current;
    const rahmen = el?.parentElement;
    if (!el || !rahmen || rahmen.scrollWidth <= rahmen.clientWidth) return;
    rahmen.scrollLeft = ((0 - props.box.x) / props.box.w) * el.clientWidth - rahmen.clientWidth / 2;
  }, [props.box.x, props.box.w]);

  // Während des Ziehens darf die Seite nicht scrollen; vorher schon (Wischen bleibt Scrollen).
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const block = (e: TouchEvent) => {
      if (zug.current?.dragging) e.preventDefault();
    };
    el.addEventListener("touchmove", block, { passive: false });
    return () => el.removeEventListener("touchmove", block);
  }, []);

  const toSvg = (cx: number, cy: number): Pt => {
    const m = svg.current?.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = new DOMPoint(cx, cy).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  };
  const griffAt = (p: Pt) => (props.griffe ?? []).find((g) => {
    const q = lerp(g.wall, g.pos);
    return Math.hypot(p.x - q.x, p.y - q.y) < 32;
  });
  const doorAt = (p: Pt) => {
    let hit: { key: string; d: number } | null = null;
    for (const t of props.doors) {
      if (t.fest) continue;
      const w = props.walls.find((x) => x.id === t.wall);
      if (!w) continue;
      const q = lerp(w, t.pos);
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d < 30 && (!hit || d < hit.d)) hit = { key: t.key, d };
    }
    return hit?.key ?? null;
  };
  const ende = () => {
    if (zug.current) window.clearTimeout(zug.current.timer);
    zug.current = null;
    setGeist(null);
    setHaltend(false);
  };

  return (
    <svg
      ref={svg}
      className={`zieh-skizze${haltend ? " zieht" : ""}`}
      viewBox={`${props.box.x} ${props.box.y} ${props.box.w} ${props.box.h}`}
      // Nie kleiner als 0,7 px je Einheit (Türen und + bleiben treffbar, großer Flur scrollt im Rahmen), nie größer als 1,1.
      style={{ minWidth: Math.round(props.box.w * 0.7), maxWidth: Math.round(props.box.w * 1.1) }}
      role="img"
      aria-label={props.label}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        const p = toSvg(e.clientX, e.clientY);
        const g = griffAt(p);
        const key = g ? g.key : doorAt(p);
        const walls = g ? g.walls : props.walls;
        const z: Zug = { key, start: { x: e.clientX, y: e.clientY }, pointerId: e.pointerId, dragging: false, timer: 0, walls };
        z.timer = window.setTimeout(() => {
          if (zug.current !== z) return;
          z.dragging = true;
          setHaltend(true);
          try {
            svg.current?.setPointerCapture(z.pointerId);
          } catch {
            // Zeiger schon weg: dann endet der Zug beim nächsten Ereignis.
          }
          navigator.vibrate?.(15);
          setGeist({ p, snap: snap(z.walls, p) });
        }, HALTEN_MS);
        zug.current = z;
      }}
      onPointerMove={(e) => {
        const z = zug.current;
        if (!z || e.pointerId !== z.pointerId) return;
        if (!z.dragging) {
          if (Math.hypot(e.clientX - z.start.x, e.clientY - z.start.y) > WACKELN_PX) ende();
          return;
        }
        const p = toSvg(e.clientX, e.clientY);
        setGeist({ p, snap: snap(z.walls, p) });
      }}
      onPointerUp={(e) => {
        const z = zug.current;
        if (!z || e.pointerId !== z.pointerId) return;
        if (z.dragging) {
          const s = snap(z.walls, toSvg(e.clientX, e.clientY));
          if (s) props.onDrop(z.key, s.wall.id, s.pos);
        } else if (z.key && !z.key.includes(":") && z.key !== "eingang") props.onTap(z.key);
        ende();
      }}
      onPointerCancel={ende}
    >
      {props.children}
      {(() => {
        // Beschriftungen nahe beieinander (z. B. zwei Türen an einer Ecke) weichen aus (31.10.2026).
        // Auch Punkte in der Skizze (Gangnummer, +, −, Ziehpunkte) freihalten.
        const punkte = [...(props.hindernisse ?? []), ...(props.griffe ?? []).map((g) => lerp(g.wall, g.pos))];
        const belegt: { x0: number; x1: number; y0: number; y1: number }[] = punkte.map((q) => ({ x0: q.x - 17, x1: q.x + 17, y0: q.y - 17, y1: q.y + 17 }));
        return props.doors.map((t) => {
          const w = props.walls.find((x) => x.id === t.wall);
          if (!w) return null;
          const bewegt = haltend && zug.current?.key === t.key;
          const lab = labelAt(w, t.pos);
          const breite = t.label.length * 10.5;
          const kasten = (dy: number) => {
            const x0 = lab.anchor === "start" ? lab.x : lab.anchor === "end" ? lab.x - breite : lab.x - breite / 2;
            return { x0, x1: x0 + breite, y0: lab.y + dy - 16, y1: lab.y + dy + 6 };
          };
          const schritt = Math.abs(w.out.y) > 0.5 ? w.out.y * 22 : 22;
          let dy = 0;
          for (let k = 0; k < 5 && belegt.some((b) => { const c = kasten(dy); return c.x0 < b.x1 && c.x1 > b.x0 && c.y0 < b.y1 && c.y1 > b.y0; }); k++) dy += schritt;
          if (t.label) belegt.push(kasten(dy));
          return <Tuer key={t.key} wall={w} pos={t.pos} nr={t.nr} label={t.label} aktiv={t.aktiv} blass={bewegt} fest={t.fest} dy={dy} />;
        });
      })()}
      {(props.griffe ?? []).map((g) => {
        const q = lerp(g.wall, g.pos);
        const bewegt = haltend && zug.current?.key === g.key;
        return (
          <g key={g.key} className={`griff ${g.art}${bewegt ? " blass" : ""}`}>
            <circle cx={q.x} cy={q.y} r="16" />
            <text x={q.x} y={q.y + 5} textAnchor="middle">{g.label}</text>
          </g>
        );
      })}
      {geist &&
        (geist.snap && zug.current?.key && (props.griffe ?? []).some((g) => g.key === zug.current?.key) ? (
          <g className="griff geist">
            <circle cx={lerp(geist.snap.wall, geist.snap.pos).x} cy={lerp(geist.snap.wall, geist.snap.pos).y} r="16" />
          </g>
        ) : geist.snap ? (
          <Tuer wall={geist.snap.wall} pos={geist.snap.pos} nr={zug.current?.key ? (props.doors.find((t) => t.key === zug.current?.key)?.nr ?? "+") : "+"} label="" geist />
        ) : (
          <circle cx={geist.p.x} cy={geist.p.y} r="12" className="geist-frei" />
        ))}
    </svg>
  );
}

/** Lage der Beschriftung einer Tür: außen vor der Wand (Grundlinie), Ausrichtung je nach Wand. */
function labelAt(w: Wand, pos: number): { x: number; y: number; anchor: "start" | "end" | "middle" } {
  const c = lerp(w, pos);
  // Eingangswand (30.10.2026): Beschriftung seitlich nach außen, unter der Wand steht schon „Wohnungstür“.
  const seitlich = w.id === "1:anfang" ? (pos < 0.5 ? -1 : 1) : 0;
  if (seitlich) return { x: c.x + seitlich * 24, y: c.y - 6, anchor: seitlich < 0 ? "end" : "start" };
  const anchor = w.out.x > 0.5 ? "start" : w.out.x < -0.5 ? "end" : "middle";
  return { x: c.x + w.out.x * 24, y: c.y + w.out.y * 30 + 4 + (w.out.y > 0.5 ? 6 : w.out.y < -0.5 ? -2 : 0), anchor };
}

function Tuer(props: { wall: Wand; pos: number; nr: string; label: string; aktiv?: boolean; blass?: boolean; geist?: boolean; fest?: boolean; dy?: number }) {
  const w = props.wall;
  const len = Math.hypot(w.b.x - w.a.x, w.b.y - w.a.y) || 1;
  const ux = (w.b.x - w.a.x) / len;
  const uy = (w.b.y - w.a.y) / len;
  const c = lerp(w, props.pos);
  const lab = labelAt(w, props.pos);
  const cls = `tuer${props.aktiv ? " aktiv" : ""}${props.blass ? " blass" : ""}${props.geist ? " geist" : ""}${props.fest ? " fest" : ""}`;
  return (
    <g className={cls}>
      <line x1={c.x - ux * TUER_HALB} y1={c.y - uy * TUER_HALB} x2={c.x + ux * TUER_HALB} y2={c.y + uy * TUER_HALB} className="tuer-blatt" />
      <circle cx={c.x + w.out.x * 2} cy={c.y + w.out.y * 2} r="14" className="tuer-punkt" />
      <text x={c.x + w.out.x * 2} y={c.y + w.out.y * 2 + 4} textAnchor="middle" className="tuer-nr-text">
        {props.nr}
      </text>
      {props.label && (
        <text x={lab.x} y={lab.y + (props.dy ?? 0)} textAnchor={lab.anchor} className="tuer-label">
          {props.label}
        </text>
      )}
    </g>
  );
}
